<template>
	<div
		:class="[
			'min-h-screen transition-colors duration-300 font-[\'Inter\',sans-serif]',
			isLight ? 'bg-[#e4f4f0]' : 'bg-[#0f1f1c]',
		]">
		<NavBarComponent :isLight="isLight" />

		<!-- HERO -->
		<section class="relative overflow-hidden pt-32 pb-12 px-10">
			<div
				class="absolute -top-20 left-[40%] w-[700px] h-[500px] pointer-events-none"
				:class="
					isLight
						? 'bg-[radial-gradient(ellipse,rgba(26,155,138,0.14)_0%,transparent_65%)]'
						: 'bg-[radial-gradient(ellipse,rgba(26,155,138,0.08)_0%,transparent_65%)]'
				"></div>

			<div
				class="absolute inset-0 overflow-hidden pointer-events-none"
				aria-hidden="true">
				<svg
					class="absolute -top-10 -right-10 w-[420px] h-[420px] pointer-events-none"
					:style="{ opacity: isLight ? 0.12 : 0.1 }"
					viewBox="0 0 160 160"
					fill="none"
					xmlns="http://www.w3.org/2000/svg">
					<g :fill="isLight ? '#0d6b5e' : '#a8d5cc'">
						<rect x="40"  y="0"   width="28" height="28" transform="rotate(45 40 0)"   />
						<rect x="80"  y="0"   width="28" height="28" transform="rotate(45 80 0)"   />
						<rect x="120" y="0"   width="28" height="28" transform="rotate(45 120 0)"  />
						<rect x="20"  y="40"  width="28" height="28" transform="rotate(45 20 40)"  />
						<rect x="60"  y="40"  width="28" height="28" transform="rotate(45 60 40)"  />
						<rect x="100" y="40"  width="28" height="28" transform="rotate(45 100 40)" />
						<rect x="140" y="40"  width="28" height="28" transform="rotate(45 140 40)" />
						<rect x="40"  y="80"  width="28" height="28" transform="rotate(45 40 80)"  />
						<rect x="80"  y="80"  width="28" height="28" transform="rotate(45 80 80)"  />
						<rect x="120" y="80"  width="28" height="28" transform="rotate(45 120 80)" />
						<rect x="20"  y="120" width="20" height="20" transform="rotate(45 20 120)" />
						<rect x="60"  y="120" width="20" height="20" transform="rotate(45 60 120)" />
						<rect x="100" y="120" width="20" height="20" transform="rotate(45 100 120)"/>
						<rect x="140" y="120" width="20" height="20" transform="rotate(45 140 120)"/>
					</g>
				</svg>

				<svg
					class="absolute -bottom-10 -left-10 w-[280px] h-[280px] pointer-events-none"
					:style="{ opacity: isLight ? 0.09 : 0.08 }"
					viewBox="0 0 120 120"
					fill="none"
					xmlns="http://www.w3.org/2000/svg">
					<g
						:stroke="isLight ? '#0f1f1c' : '#a8d5cc'"
						stroke-width="3"
						fill="none">
						<rect x="60" y="10" width="64" height="64" transform="rotate(45 60 10)" />
						<rect x="60" y="22" width="46" height="46" transform="rotate(45 60 22)" />
						<rect x="60" y="34" width="28" height="28" transform="rotate(45 60 34)" />
						<rect x="60" y="46" width="10" height="10" transform="rotate(45 60 46)" />
					</g>
				</svg>
			</div>

			<div
				class="relative z-10 inline-flex items-center gap-2 mb-6 text-[11px] font-semibold tracking-widest uppercase border rounded-full px-4 py-1.5"
				:class="
					isLight
						? 'text-[#0d6b5e] bg-[#1a9b8a]/10 border-[#1a9b8a]/30'
						: 'text-[#a8d5cc] bg-[#1a9b8a]/10 border-[#1a9b8a]/25'
				">
				<span class="w-1.5 h-1.5 rounded-full animate-pulseFast bg-[#1a9b8a]"></span>
				✦ {{ $t('discover.badge') }}
			</div>

			<h1
				class="relative z-10 font-['Montserrat'] font-extrabold leading-[1.04] tracking-[-2px] max-w-1xl mb-8"
				:class="[
					'text-[clamp(34px,5vw,66px)]',
					isLight ? 'text-[#0f1f1c]' : 'text-white',
				]">
				{{ $t('discover.headline') }}<br />
				{{ $t('discover.for') }}
				<em
					class="not-italic"
					:class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'"
					>{{ $t('discover.highlight') }}</em
				>
			</h1>

			<div class="relative z-10">
				<SearchComponent :isLight="isLight" />
			</div>
		</section>

		<!-- Filters + View toggle -->
		<FilterBarComponent
			:isLight="isLight"
			:activeCategory="activeCategory"
			:activeTime="activeTime"
			:activeView="activeView"
			:pickedDate="pickedDate"
      :hasActiveFilters="hasActiveFilters"
			@category-change="activeCategory = $event"
			@time-change="onTimeChange"
			@view-change="activeView = $event"
			@date-change="pickedDate = $event"
			@reset-filters="resetFilters" />

		<!-- Views -->
		<EventsGridComponent
			v-if="activeView === 'list'"
			:events="filteredEvents"
			:isLight="isLight" 
			:isLoading="isLoading"
			/>
		<MapComponent v-else :searchTerm="searchTerm" :isLight="isLight" :events="filteredEvents" @viewport-change="fetchEventsForViewport"/>

		<!-- Footer -->
		<FooterComponent :isLight="isLight" @toggle-mode="toggleMode" />
	</div>
<PWAInstallPrompt />
</template>

<script>
import { useHead } from "@vueuse/head";
import { useTheme } from "@/composables/useTheme";

import NavBarComponent from "@/components/NavBarComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import SearchComponent from "@/components/SearchComponent.vue";
import MapComponent from "@/components/MapComponent.vue";
import FilterBarComponent from "@/components/FilterBarComponent.vue";
import EventsGridComponent from "@/components/EventsGridComponent.vue";
import PWAInstallPrompt from "@/components/PWAInstallPrompt.vue";


import apiService from "@/services/api";

export default {
	name: "DiscoverView",

	components: {
		NavBarComponent,
		FooterComponent,
		SearchComponent,
		MapComponent,
		FilterBarComponent,
		EventsGridComponent,
		PWAInstallPrompt
	},

	setup() {
		const { isLight, toggleMode } =
			useTheme();

		return {
			isLight,
			toggleMode,
		};
	},

	data() {
		return {
			searchTerm: "",

			activeView: "list",

			activeCategory: "All",

			activeTime: null,

			pickedDate: null,

			events: [],

			isLoading: false,

			activeEvents: [],

			activeEventsPage: 1,

			itemsPerPage: 12,

			paginatedActiveEvents: [],
		};
	},

	computed: {
		// ─────────────────────────────────────────────
		// ACTIVE FILTERS
		// ─────────────────────────────────────────────
		hasActiveFilters() {
			return (
				this.activeCategory !==
					"All" ||
				this.activeTime !==
					null ||
				this.pickedDate !== null
			);
		},

		// ─────────────────────────────────────────────
		// FILTERED EVENTS
		// ─────────────────────────────────────────────
		filteredEvents() {
			const now = new Date();

			let result = [
				...this.activeEvents,
			];

			// CATEGORY
			if (
				this.activeCategory &&
				this.activeCategory !==
					"All"
			) {
				result = result.filter(
					(e) => {
						if (
							!Array.isArray(
								e.category
							)
						)
							return false;

						return e.category.some(
							(c) => {
								const name =
									(
										c.Name ||
										c.name ||
										c
									).toLowerCase();

								return (
									name ===
									this.activeCategory.toLowerCase()
								);
							}
						);
					}
				);
			}

			// DATE HELPERS
			const startOfDay = (d) =>
				new Date(
					d.getFullYear(),
					d.getMonth(),
					d.getDate(),
					0,
					0,
					0,
					0
				);

			const endOfDay = (d) =>
				new Date(
					d.getFullYear(),
					d.getMonth(),
					d.getDate(),
					23,
					59,
					59,
					999
				);

			const today =
				startOfDay(now);

			const tomorrow =
				new Date(today);

			tomorrow.setDate(
				tomorrow.getDate() + 1
			);

			const tomorrowEnd =
				endOfDay(tomorrow);

			// WEEKEND
			const dayOfWeek =
				now.getDay();

			const diffToSat =
				(6 -
					dayOfWeek +
					7) %
				7;

			const weekendStart =
				new Date(today);

			weekendStart.setDate(
				today.getDate() +
					diffToSat
			);

			const weekendEnd =
				endOfDay(
					new Date(
						weekendStart.getFullYear(),
						weekendStart.getMonth(),
						weekendStart.getDate() +
							1
					)
				);

			// NEXT WEEK
			const diffToMon =
				(1 -
					dayOfWeek +
					7) %
				7;

			const nextWeekStart =
				new Date(today);

			nextWeekStart.setDate(
				today.getDate() +
					diffToMon
			);

			const nextWeekEnd =
				endOfDay(
					new Date(
						nextWeekStart.getFullYear(),
						nextWeekStart.getMonth(),
						nextWeekStart.getDate() +
							6
					)
				);

			// MONTH
			const monthStart =
				new Date(
					now.getFullYear(),
					now.getMonth(),
					1
				);

			const monthEnd =
				new Date(
					now.getFullYear(),
					now.getMonth() +
						1,
					0,
					23,
					59,
					59,
					999
				);

			// OVERLAP
			const eventOverlaps = (
				event,
				from,
				to
			) => {
				return (
					event.start <= to &&
					event.end >= from
				);
			};

			// TIME FILTERS
			switch (
				this.activeTime
			) {
				case "Happening Now":
					result =
						result.filter(
							(e) =>
								now >=
									e.start &&
								now <=
									e.end
						);
					break;

				case "Today":
					result =
						result.filter(
							(e) =>
								eventOverlaps(
									e,
									today,
									endOfDay(
										now
									)
								)
						);
					break;

				case "Tomorrow":
					result =
						result.filter(
							(e) =>
								eventOverlaps(
									e,
									tomorrow,
									tomorrowEnd
								)
						);
					break;

				case "This Weekend":
					result =
						result.filter(
							(e) =>
								eventOverlaps(
									e,
									weekendStart,
									weekendEnd
								)
						);
					break;

				case "Next Week":
					result =
						result.filter(
							(e) =>
								eventOverlaps(
									e,
									nextWeekStart,
									nextWeekEnd
								)
						);
					break;

				case "This Month":
					result =
						result.filter(
							(e) =>
								eventOverlaps(
									e,
									monthStart,
									monthEnd
								)
						);
					break;

				case "Pick Date ›":
					if (
						this.pickedDate
					) {
						const picked =
							this.parseEventDate(
								this
									.pickedDate
							);

						const pickedEnd =
							endOfDay(
								picked
							);

						result =
							result.filter(
								(e) =>
									eventOverlaps(
										e,
										picked,
										pickedEnd
									)
							);
					}
					break;
			}

			return result;
		},
	},

	created() {
		this.fetchEvents();
	},

	methods: {
		// ─────────────────────────────────────────────
		// SAFE DATE PARSER
		// ─────────────────────────────────────────────
		parseEventDate(
			dateStr,
			timeStr = "00:00"
		) {
			if (!dateStr)
				return null;

			const pureDate =
				dateStr.split(
					"T"
				)[0];

			const [
				year,
				month,
				day,
			] = pureDate
				.split("-")
				.map(Number);

			const [
				hours,
				minutes,
			] = timeStr
				.split(":")
				.map(Number);

			return new Date(
				year,
				month - 1,
				day,
				hours || 0,
				minutes || 0,
				0,
				0
			);
		},

		// ─────────────────────────────────────────────
		// TIME CHANGE
		// ─────────────────────────────────────────────
		onTimeChange(t) {
			this.activeTime = t;

			if (
				t !==
				"Pick Date ›"
			) {
				this.pickedDate =
					null;
			}
		},

		// ─────────────────────────────────────────────
		// RESET FILTERS
		// ─────────────────────────────────────────────
		resetFilters() {
			this.activeCategory =
				"All";

			this.activeTime =
				null;

			this.pickedDate =
				null;
		},

		// ─────────────────────────────────────────────
		// EVENT STATUS
		// ─────────────────────────────────────────────
		getEventStatus(
			start,
			end
		) {
			const now =
				new Date();

			if (
				!start ||
				!end
			) {
				return {
					status:
						"Invalid Date",

					color:
						"bg-gray-500",
				};
			}

			if (
				now >= start &&
				now <= end
			) {
				return {
					status:
						"LIVE",

					color:
						"bg-green-500",
				};
			}

			if (now > end) {
				return {
					status:
						"ENDED",

					color:
						"bg-red-500",
				};
			}

			if (
				now < start &&
				start - now <=
					24 *
						60 *
						60 *
						1000
			) {
				return {
					status:
						"STARTING SOON",

					color:
						"bg-yellow-500",
				};
			}

			return {
				status:
					"RSVP",

				color:
					"bg-blue-500",
			};
		},

		// ─────────────────────────────────────────────
		// NORMALIZE EVENT
		// ─────────────────────────────────────────────
		normalizeEvent(
			item
		) {
			const start =
				this.parseEventDate(
					item.date,
					item.start_time
				);

			const end =
				this.parseEventDate(
					item.end_date,
					item.end_time
				);

			const statusObj =
				this.getEventStatus(
					start,
					end
				);

			const statusMap = {
				LIVE: "live",

				"STARTING SOON":
					"soon",

				RSVP: "rsvp",

				ENDED: "ended",
			};

			const cardStatus =
				statusMap[
					statusObj.status
				] || "rsvp";

			const titleStr =
				item.title ||
				"Untitled Event";

			const priceNum =
				parseFloat(
					item.tickets?.[0]
						?.Amount
				) || 0;

			return {
				id: item.event_id,

				title: titleStr,

				location:
					item.location,

				date: item.date,

				endDate:
					item.end_date,

				endTime:
					item.end_time,

				startTime:
					item.start_time,

				start,
				end,

				description:
					item.description,

				imageUrl:
					item.image_url,

				organizer:
					item.organizer,

				capacity:
					item.capacity,

				category:
					item.categories,

				eventType:
					item.event_type,

				longitude:
					item.longitude,

				latitude:
					item.latitude,

				location_name:
					item.location_name,

				organizerLogos:
					Array.isArray(
						item.organizer_logos
					)
						? item.organizer_logos.map(
								(logo) => ({
									url:logo.LogoURL ||"https://www.adaptivewfs.com/wp-content/uploads/2020/07/logo-placeholder-image.png",
								}))
						: [],

				Title:
					titleStr,

				ImageURL:
					item.image_url ||
					null,

				posterGhost:
					titleStr.toUpperCase(),

				Description:
					item.description ||
					"",

				venue:
					item.location ||
					"",

				price:
					priceNum === 0
						? this.$t('common.free')
						: `$${priceNum.toFixed(2)}`,

				priceSub:
					item.tickets?.[0]
						?.Type ||
					"",

				tags:
					Array.isArray(
						item.categories
					)
						? item.categories.map(
								(
									c
								) =>
									`#${
										c.Name ||
										c.name ||
										c
									}`)
						: [],

				status:
					cardStatus,

				attendees:
					[],

				attendeeCount:
					"",

				saved: false,
			};
		},

		// ─────────────────────────────────────────────
		// FETCH EVENTS
		// ─────────────────────────────────────────────
		async fetchEvents() {
			try {
				this.isLoading = true;
				const response =
					await apiService.getEvents();

				if (
					response.data &&
					Array.isArray(
						response.data.data
					)
				) {
					const currentDate =
						new Date();

					const normalized =
						response.data.data.map(
							(item) =>
								this.normalizeEvent(
									item
								)
						);

					this.events =
						normalized;

					this.activeEvents =
						normalized.filter(
							(
								event
							) =>
								event.end >=
								currentDate
						);

					this.updatePaginatedEvents();
				} else {
					console.error(
						"Invalid response from API"
					);

					this.events = [];

					this.activeEvents =
						[];
				}
			} catch (error) {
				console.error(
					error
				);
			} finally {
				this.isLoading = false;
			}
		},

		// ─────────────────────────────────────────────
		// PAGINATION
		// ─────────────────────────────────────────────
		updatePaginatedEvents() {
			const start =
				(this.activeEventsPage -
					1) *
				this.itemsPerPage;

			this.paginatedActiveEvents =
				this.activeEvents.slice(
					start,
					start +
						this.itemsPerPage
				);
		},
	},

  mounted() {
  useHead({
    title: "Discover — Trekko",

    meta: [
      // ── Core SEO ──────────────────────────────────────
      {
        name: "description",
        content:
          "Discover local events happening near you. Browse concerts, festivals, sports, and more — filter by date, category, and location on Trekko.",
      },
      {
        name: "keywords",
        content:
          "events near me, local events, concerts, festivals, things to do, Trekko, event discovery, nearby events, event finder, local happenings, event search,roma, italy, milan, naples, turin, palermo, genoa, bologna, florence, bari, catania, venice, verona, messina, padua, trieste, taranto, brescia, parma, modena, reggio emilia, perugia, livorno, salvador, recife, fortaleza, belo horizonte, munich, berlin, hamburg, cologne, frankfurt, stuttgart, düsseldorf, dortmund, essen, leipzig, bremen, dresden, hannover, nuremberg",
      },
      {
        name: "robots",
        content: "index, follow",
      },

      // ── Open Graph (Facebook / LinkedIn / WhatsApp) ───
      {
        property: "og:type",
        content: "website",
      },
      {
        property: "og:site_name",
        content: "Trekko",
      },
      {
        property: "og:title",
        content: "Discover Events Near You — Trekko",
      },
      {
        property: "og:description",
        content:
          "Explore endless possibilities. Find concerts, festivals, and local events happening around you — all in one place.",
      },
      {
        property: "og:image",
        content: "https://trekko.xyz/og-image.jpg", // 1200×630 recommended
      },
      {
        property: "og:url",
        content: "https://trekko.xyz",
      },

      // ── Twitter Card ──────────────────────────────────
      {
        name: "twitter:card",
        content: "summary_large_image",
      },
      {
        name: "twitter:site",
        content: "@gotrekko", // your Twitter handle
      },
      {
        name: "twitter:creator",
        content: "@gotrekko",
      },
      {
        name: "twitter:title",
        content: "Discover Events Near You — Trekko",
      },
      {
        name: "twitter:description",
        content:
          "Explore endless possibilities. Find concerts, festivals, and local events happening around you — all in one place.",
      },
      {
        name: "twitter:image",
        content: "https://trekko.xyz/og-image.jpg", // min 300×157, max 4096×4096
      },
      {
        name: "twitter:image:alt",
        content: "Trekko – Discover local events near you",
      },
    ],

    link: [
      {
        rel: "canonical",
        href: "https://trekko.xyz",
      },
    ],
  });

		useHead({
			title: "Discover — Trekko",

			meta: [
				{
					name: "description",

					content:
						"Explore endless possibilities with our search feature!",
				},

				{
					property:
						"og:title",

					content:
						"Discover — Trekko",
				},
			],
		});
	},
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&family=Inter:wght@300;400;500&display=swap");
</style>